import React, { useContext, useRef, useState, useEffect} from 'react'
import './Navbar.css'
import logo from '../Assets/jenitaslogo-small-height.png'
import cart_icon from '../Assets/shopping-cart.png'
import user_icon from '../Assets/user-128px.png'
import user_logout from '../Assets/logout-32x32px.png'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { ShopContext } from '../../Context/ShopContext'
import { groupBySection } from './NavConfig'

//navbar items with their dropdown menus
const NAV_ITEMS = [
    { label: 'Home', path: '/', category: null },
    { label: 'Shop', path: '/shop-products', category: null },
    { label: 'Men', path: '/mens', category: 'men'  },
    { label: 'Women', path: '/womens', category: 'women'},
    { label: 'Other', path: '/other', category: 'other'},
]

const Navbar = () => {
  const { getTotalCartCount } = useContext(ShopContext);
  const navigate = useNavigate();
  const location = useLocation();

  // Mobile menu open state
  const [mobileOpen,    setMobileOpen] = useState(false)
  // Which mobile accordion is expanded
  const [mobileExpanded, setMobileExpanded] = useState(null)
  // Search query
  const [searchQuery, setSearchQuery] = useState('')
  // Subcategories grouped by section — keyed by category
  const [megaData, setMegaData] = useState({})
  const [userDropOpen, setUserDropOpen] = useState(false)

  const userDropRef = useRef(null)
  const searchRef = useRef(null)

  const isLoggedIn = !!localStorage.getItem('auth-token')
  const username = localStorage.getItem('username') || ''


  // Fetch all subcategories once on mount, group them by section
  useEffect(() => {
    const CATS = ['men', 'women', 'kids', 'other']
    Promise.all(
        CATS.map(cat =>
          fetch(`${process.env.REACT_APP_API_URL}/subcategories?category=${cat}`)
            .then(r => r.json())
            .then(d => ({ cat, subs: d.success ? d.subcategories : [] }))
            .catch(() => ({ cat, subs: [] }))
        )
    ).then(results => {
      const data = {}
      results.forEach(({ cat, subs }) => {
        data[cat] = groupBySection(subs, cat)
      })
      setMegaData(data)
    })
  }, [])

  // Close user dropdown when clicking outside
  useEffect(() => {
    const handler = (e) => {
      if (userDropRef.current && !userDropRef.current.contains(e.target)) {
        setUserDropOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  // Close user dropdown on route change
  useEffect(() => { setUserDropOpen(false) }, [location.pathname])


  const isActive = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  }

  const handleNav = (path) => {
    navigate(path);
    setMobileOpen(false)
    setMobileExpanded(null)
  }

  const handleSearch = (e) => {
    e.preventDefault()
    if (!searchQuery.trim()) return
    navigate(`/shop-products?search=${encodeURIComponent(searchQuery.trim())}`)
    setSearchQuery('')
    setMobileOpen(false)
  }

  const handleUserIconClick = () => {
    if (!isLoggedIn) {
      navigate('/login')
      return
    }
    setUserDropOpen(p => !p)
  }
 
  const handleLogout = () => {
    localStorage.removeItem('auth-token')
    localStorage.removeItem('username')
    setUserDropOpen(false)
    window.location.replace('/')
  }

  const toggleMobileAccordion = (label) => {
    setMobileExpanded(prev => prev === label ? null : label)
  }

  return (
    <>
      {/* navbar */}
      <nav className='navbar'>

        {/* ── Left: nav menu ── */}
        <div className='nav-left'>
          {/* Mobile hamburger */}
          <button className='nav-hamburger' onClick={() => setMobileOpen(true)} aria-label="Open menu">
            <span /><span /><span />
          </button>

          {/* Desktop nav */}
          <ul className='nav-menu'>
            {NAV_ITEMS.map(item => {
              const sections = item.category ? (megaData[item.category] || []) : []
              const hasMega  = sections.length > 0
              return (
                <li 
                  key={item.label}
                  className={`nav-item ${isActive(item.path) ? 'nav-active' : ''}`}
                  onClick={() => handleNav(item.path)} 
                >
                  {item.label}

                  {/* Mega dropdown */}
                  {hasMega && (
                      <div className='nav-mega' onClick={e => e.stopPropagation()}>
                          <div className='nav-mega-inner'>
                              {sections.map(sec => (
                                  <div key={sec.section} className='nav-mega-col'>
                                      <p className='nav-mega-section-title'>{sec.section}</p>
                                      <ul className='nav-mega-list'>
                                          {sec.items.map(sub => (
                                              <li
                                                  key={sub.name}
                                                  className='nav-mega-item'
                                                  onClick={() => handleNav(sub.path)}
                                              >
                                                  {sub.name}
                                              </li>
                                          ))}
                                      </ul>
                                  </div>
                              ))}
                              {/* Shop All link — last column */}
                              <div className='nav-mega-col nav-mega-col--shopall'>
                                  <p className='nav-mega-section-title'>Browse All</p>
                                  <div
                                      className='nav-mega-shopall-btn'
                                      onClick={() => handleNav(item.path)}
                                  >
                                      Shop All {item.label} →
                                  </div>
                              </div>
                          </div>
                      </div>
                  )}

                  {/* Regular small dropdown for Shop (no subcategories) */}
                  {!hasMega && item.path === 'shop-products' && (
                    <div className='nav-dropdown'>
                      {[
                        { label: 'New Arrivals', path: '/shop-products?tags=New' },
                        { label: 'Sale',         path: '/shop-products?tags=Sale' },
                        { label: 'Featured',     path: '/shop-products?tags=Featured' },
                      ].map(sub => (
                        <div
                          key={sub.label}
                          className='nav-dropdown-item'
                          onClick={e => { e.stopPropagation(); handleNav(sub.path) }}
                        >
                          {sub.label}
                        </div>
                      ))}
                    </div>
                  )}
                </li>
              )
            })}
          </ul>
        </div>

        {/* ── Center: logo ── */}
        <div className='nav-center'>
          <Link to='/'><img src={logo} alt='Jenitas' className='nav-logo-img' /></Link>
        </div>

        {/* Right: search / icons */}
        <div className="nav-right">
          {/* Inline search */}
          <form className='nav-search-form' onSubmit={handleSearch}>
            <input
                ref={searchRef}
                type='text'
                className='nav-search-input'
                placeholder='Search...'
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
            />
            
            <button type='submit' className='nav-search-btn' aria-label='Search'>
              <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' width='16' height='16'>
                  <circle cx='11' cy='11' r='8'/><line x1='21' y1='21' x2='16.65' y2='16.65'/>
              </svg>
            </button>
          </form>

          {/* User icon and dropdown */}
          <div className='nav-user-wrap' ref={userDropRef}>
            <img
              src={user_icon}
              alt='Account'
              className={`nav-icon ${isLoggedIn ? 'nav-icon-active' : ''}`}
              onClick={handleUserIconClick}
            />

            {/* Dropdown — only renders when logged in and open */}
            {isLoggedIn && (
              <div className={`nav-user-dropdown ${userDropOpen ? 'nav-user-dropdown--open' : ''}`}>
                {/* Greeting */}
                <div className='nav-user-greeting'>
                    <span className='nav-user-hi'>Hi,</span>
                    <span className='nav-user-name'>{username}</span>
                </div>
                <hr className='nav-user-divider' />
                {/* My Orders */}
                <div className='nav-user-item' onClick={() => { handleNav('/profile/orders'); setUserDropOpen(false) }}>
                  <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.8' width='15' height='15'>
                    <path d='M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2'/>
                    <rect x='9' y='3' width='6' height='4' rx='1'/>
                    <line x1='9' y1='12' x2='15' y2='12'/>
                    <line x1='9' y1='16' x2='13' y2='16'/>
                  </svg>
                  My Orders
                </div>
                <hr className='nav-user-divider' />
                {/* Logout */}
                <div className='nav-user-item nav-user-item--logout' onClick={handleLogout}>
                    <img src={user_logout} alt='' width='15' height='15' style={{ opacity: 0.7 }} />
                    Logout
                </div>
              </div>
            )}
          </div>

          {/* Cart */}
          <div className='nav-cart-wrap'>
            <Link to='/cart'>
              <img src={cart_icon} alt='Cart' className={`nav-icon ${isActive('/cart') ? 'nav-icon-active' : ''}`} />
            </Link>
            {getTotalCartCount() > 0 && (
              <div className='nav-cart-count'>{getTotalCartCount()}</div>
            )}
          </div>
        </div>
      </nav>

      {/* ── Mobile backdrop ── */}
      <div className={`nav-backdrop ${mobileOpen ? 'nav-backdrop--open' : ''}`} onClick={() => setMobileOpen(false)}/>

      {/* ── Mobile slide-in panel ── */}
      <div className={`nav-mobile-panel ${mobileOpen ? 'nav-mobile-panel--open' : ''}`}>
        {/* Panel header */}
        <div className='nav-mobile-header'>
          <img src={logo} alt='Jenitas' className='nav-mobile-logo' />
          <button className='nav-mobile-close' onClick={() => setMobileOpen(false)} aria-label='Close menu'>✕</button>
        </div>

        {/* Mobile search */}
        <form className='nav-mobile-search' onSubmit={handleSearch}>
          <input type='text' placeholder='Search products...' value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
          <button type='submit' aria-label='Search'>
              <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' width='16' height='16'>
                  <circle cx='11' cy='11' r='8'/><line x1='21' y1='21' x2='16.65' y2='16.65'/>
              </svg>
          </button>
        </form>

         {/* Mobile nav links */}
          <ul className='nav-mobile-menu'>
            {NAV_ITEMS.map(item => {
              const sections = item.category ? (megaData[item.category] || []) : []
              const allSubs = sections.flatMap(s => s.items)
              const hasSubs = allSubs.length > 0
              return (
                <li key={item.label} className='nav-mobile-item'>
                  <div className='nav-mobile-row'>
                    <span
                      className={`nav-mobile-link ${isActive(item.path) ? 'nav-mobile-link--active' : ''}`}
                      onClick={() => handleNav(item.path)}
                    >
                      {item.label}
                    </span>
                    {hasSubs && (
                      <button
                        className={`nav-mobile-toggle ${mobileExpanded === item.label ? 'nav-mobile-toggle--open' : ''}`}
                        onClick={() => toggleMobileAccordion(item.label)}
                        aria-label='Expand'
                      >+</button>
                    )}
                  </div>

                  {/* Mobile sub-items — flat list from all sections */}
                  {hasSubs && (
                    <ul className={`nav-mobile-sub ${mobileExpanded === item.label ? 'nav-mobile-sub--open' : ''}`}>
                      {sections.map(sec => (
                        <React.Fragment key={sec.section}>
                          <li className='nav-mobile-sub-section'>{sec.section}</li>
                          {sec.items.map(sub => (
                            <li
                              key={sub.name}
                              className='nav-mobile-sub-item'
                              onClick={() => handleNav(sub.path)}
                            >
                              {sub.name}
                            </li>
                          ))}
                        </React.Fragment>
                      ))}
                    </ul>
                  )}
                </li>
              )
            })}
          </ul>

          {/* ── Mobile user section at bottom of panel ── */}
          <div className='nav-mobile-user'>
            {isLoggedIn ? (
              <>
                <p className='nav-mobile-user-greeting'>Hi, {username}</p>
                <div className='nav-mobile-user-item' onClick={() => handleNav('/profile/orders')}>
                  My Orders
                </div>
                <div className='nav-mobile-user-item nav-mobile-user-item--logout' onClick={handleLogout}>
                  Logout
                </div>
              </>
            ) : (
              <div className='nav-mobile-user-item' onClick={() => handleNav('/login')}>
                Login / Register
              </div>
            )}
          </div>
      </div>

    </>
  )
}

export default Navbar